"use client";

import * as React from "react";
import { cn, ErrorState, type ErrorStateProps } from "@aio/platform-ui";

/**
 * Props for {@link ErrorTable}.
 *
 * Forwards every {@link ErrorStateProps} field to the underlying
 * `@aio/platform-ui` `ErrorState`, with one extra:
 *
 * - **`colSpan`**: when provided, renders the error state inside a
 *   `<tr><td colSpan={colSpan}>` so it can sit inside an otherwise-empty
 *   `<tbody>` (matching the {@link EmptyTable} pattern).
 *
 * The `onRetry` callback is the only thing the caller is generally
 * expected to provide; everything else has sensible defaults inherited
 * from `ErrorState`.
 */
export interface ErrorTableProps extends ErrorStateProps {
  /**
   * If provided, render the error state as a `<tr><td colSpan={colSpan}>`
   * suitable for slotting into an empty `<tbody>`. The number must match
   * the visible column count.
   */
  colSpan?: number;
  /** Test id forwarded to the outermost element (`<tr>` or `<div>`). */
  "data-testid"?: string;
}

/**
 * Error-state wrapper for the table engine.
 *
 * Used when a `dataSource` Promise rejects, or when a downstream filter
 * coercion throws (e.g. malformed date filter from a shared URL). The
 * caller is responsible for providing the `onRetry` handler; passing
 * `undefined` hides the retry button entirely (mirrors `ErrorState`).
 *
 * Like {@link EmptyTable}, two render modes are available:
 * - Standalone `<div>` (default).
 * - In-table `<tr><td colSpan>` when the engine wants to keep the table
 *   chrome (header, footer, layout) intact during the failure.
 */
export function ErrorTable({
  title,
  description,
  onRetry,
  retryLabel,
  traceId,
  colSpan,
  className,
  "data-testid": dataTestId = "error-table",
}: ErrorTableProps): React.JSX.Element {
  // We forward every prop conditionally because `ErrorState` already
  // assigns defaults internally — passing `undefined` here would override
  // those defaults under exactOptionalPropertyTypes.
  const panel = (
    <ErrorState
      {...(title !== undefined ? { title } : {})}
      {...(description !== undefined ? { description } : {})}
      {...(onRetry !== undefined ? { onRetry } : {})}
      {...(retryLabel !== undefined ? { retryLabel } : {})}
      {...(traceId !== undefined ? { traceId } : {})}
      // Strip the panel-style border in colSpan mode so the embedded
      // error reads as part of the table, not a sibling block.
      className={cn(
        colSpan !== undefined ? "border-0" : undefined,
        className,
      )}
    />
  );

  if (colSpan !== undefined) {
    return (
      <tr data-testid={dataTestId}>
        <td colSpan={colSpan} className="p-0">
          {panel}
        </td>
      </tr>
    );
  }

  return (
    <div className="w-full" data-testid={dataTestId}>
      {panel}
    </div>
  );
}
